/**
 * SOPAIGenerator — prompt or voice → AI-generated SOP steps, editable in a
 * preview list, then bulk-inserted into the SOP.
 */
import { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Sparkles, Mic, Square, Trash2, RefreshCw, Plus } from "lucide-react";

const KINDS = ["checkbox", "form", "upload", "navigate", "embed", "video"];

export default function SOPAIGenerator({ sopId, wsId, sopTitle, onDone, onClose }) {
  const [prompt, setPrompt] = useState("");
  const [steps, setSteps] = useState(null); // null = not generated yet
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recRef = useRef(null);

  const toggleRecord = async () => {
    if (recording) {
      recRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : undefined });
      const chunks = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
        if (blob.size < 1000) { toast.error("Recording too short"); return; }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "sop-voice.webm");
          const { data } = await api.post("/sops/transcribe", fd);
          setPrompt((p) => (p ? `${p}\n${data.text}` : data.text));
          toast.success("Voice transcribed");
        } catch (e) { toast.error(apiErrorMessage(e, "Transcription failed")); }
        finally { setTranscribing(false); }
      };
      mr.start();
      recRef.current = mr;
      setRecording(true);
    } catch {
      toast.error("Microphone access denied");
    }
  };

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Describe the process first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/sops/ai-generate-steps`, {
        prompt: prompt.trim(), sop_title: sopTitle,
      });
      setSteps(data.steps || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Generation failed")); }
    finally { setBusy(false); }
  };

  const setStep = (i, k, v) => setSteps((arr) => arr.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));
  const removeStep = (i) => setSteps((arr) => arr.filter((_, idx) => idx !== i));

  const confirm = async () => {
    if (!steps?.length) return;
    setAdding(true);
    try {
      const { data } = await api.post(`/sops/${sopId}/steps/bulk`, { steps });
      toast.success(`${data.created} steps added`);
      onDone?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to add steps")); }
    finally { setAdding(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto" data-testid="sop-ai-generator">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" /> AI Step Generator
          </DialogTitle>
        </DialogHeader>

        {!steps ? (
          <div className="space-y-3">
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">Describe the process (or dictate it)</Label>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g. New client onboarding: send welcome email, collect brand assets, set up project folder, schedule kickoff call, share the questionnaire…"
                className="min-h-[140px] text-sm"
                data-testid="sop-ai-prompt"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={recording ? "destructive" : "outline"}
                className="rounded-none"
                onClick={toggleRecord}
                disabled={transcribing}
                data-testid="sop-ai-record-btn"
              >
                {recording ? <Square className="w-4 h-4 mr-2" /> : <Mic className="w-4 h-4 mr-2" />}
                {recording ? "Stop recording" : transcribing ? "Transcribing…" : "Record voice"}
              </Button>
              {recording && <span className="text-xs text-red-600 animate-pulse">● Recording — speak your process</span>}
              <Button className="rounded-none ml-auto" onClick={generate} disabled={busy || !prompt.trim()} data-testid="sop-ai-generate-btn">
                {busy ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {busy ? "Generating…" : "Generate steps"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold">{steps.length} generated steps — edit before adding</p>
              <Button size="sm" variant="ghost" className="rounded-none h-7 text-xs" onClick={() => setSteps(null)} data-testid="sop-ai-back">
                ← Change prompt
              </Button>
            </div>
            <div className="space-y-2 max-h-[50vh] overflow-auto pr-1" data-testid="sop-ai-preview-list">
              {steps.map((s, i) => (
                <div key={i} className="border border-border p-2 space-y-1.5 bg-muted/10" data-testid={`sop-ai-step-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono text-muted-foreground w-5">{i + 1}.</span>
                    <Input value={s.title} onChange={(e) => setStep(i, "title", e.target.value)} className="h-8 rounded-none text-sm flex-1" data-testid={`sop-ai-step-title-${i}`} />
                    <Select value={s.kind} onValueChange={(v) => setStep(i, "kind", v)}>
                      <SelectTrigger className="h-8 w-28 rounded-none text-xs" data-testid={`sop-ai-step-kind-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                    </Select>
                    <label className="text-[10px] inline-flex items-center gap-1">
                      <input type="checkbox" checked={s.is_required !== false} onChange={(e) => setStep(i, "is_required", e.target.checked)} />
                      Req
                    </label>
                    <Button size="sm" variant="ghost" className="rounded-none h-7 w-7 p-0 text-red-600" onClick={() => removeStep(i)} data-testid={`sop-ai-step-del-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <Textarea
                    value={s.description || ""}
                    onChange={(e) => setStep(i, "description", e.target.value)}
                    className="min-h-[48px] text-xs rounded-none font-mono"
                    placeholder="Step instructions (HTML supported)"
                    data-testid={`sop-ai-step-desc-${i}`}
                  />
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
              <Button className="rounded-none" onClick={confirm} disabled={adding || steps.length === 0} data-testid="sop-ai-confirm-btn">
                <Plus className="w-4 h-4 mr-1" /> {adding ? "Adding…" : `Add ${steps.length} steps to SOP`}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
